/**
 * Run Custom Script – Two-column layout: controls left, script editor right,
 * execution history below. Matches the Run Custom Script page design.
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Terminal,
  Play,
  Monitor,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { apiService } from '../services/api';
import type { DeviceCapabilityMatrix } from '../services/api';
import { deviceMatchesOS } from '../utils/devicePlatformMatch';
import { HubPage, HubPanel } from './ui/HubShell';
import DevicePicker, { getDeviceId as pickerDeviceId } from './DevicePicker';
import { classifyCommandOutcome, commandOutcomeLabel } from '../utils/commandOutcome';

type TargetScope = 'single' | 'multiple' | 'group';
type ScriptType = 'powershell' | 'bash' | 'cmd' | 'shell';

const OS_OPTIONS = [
  { value: '', label: '-- Choose OS Family --' },
  { value: 'plat-windows', label: 'Windows' },
  { value: 'plat-macos', label: 'macOS' },
  { value: 'plat-linux', label: 'Linux' },
  { value: 'plat-android', label: 'Android' },
  { value: 'plat-ios', label: 'iOS' },
];

const SCRIPT_EXT: Record<ScriptType, string> = {
  powershell: 'ps1',
  bash: 'sh',
  cmd: 'cmd',
  shell: 'sh',
};

const SCRIPT_LABEL: Record<ScriptType, string> = {
  powershell: 'PowerShell Script',
  bash: 'Bash Script',
  cmd: 'CMD Script',
  shell: 'Shell Script',
};

function getDeviceId(d: any): string {
  return pickerDeviceId(d);
}

const RemoteExecutionHub: React.FC = () => {
  const [devices, setDevices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [targetOS, setTargetOS] = useState<string>('');
  const [targetScope, setTargetScope] = useState<TargetScope>('single');
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [scriptType, setScriptType] = useState<ScriptType>('powershell');
  const [scriptContent, setScriptContent] = useState('# PowerShell script');
  const [runAsAdministrator, setRunAsAdministrator] = useState(true);
  const [waitForResult, setWaitForResult] = useState(true);
  const [timeoutSeconds, setTimeoutSeconds] = useState(300);
  const [executing, setExecuting] = useState(false);
  const [lastResult, setLastResult] = useState<{ success: number; failed: number; message?: string } | null>(null);
  const [commandHistory, setCommandHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(null);
  const [lastRunOutput, setLastRunOutput] = useState<string | null>(null);
  const [capabilities, setCapabilities] = useState<DeviceCapabilityMatrix | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiService
      .getDevices()
      .then((data) => {
        if (!cancelled) setDevices(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setDevices([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const devicesForOS = useMemo(() => {
    if (!targetOS) return [];
    return devices.filter((d) => deviceMatchesOS(d, targetOS));
  }, [devices, targetOS]);

  const deviceGroups = useMemo(() => {
    const platforms = [
      { id: 'plat-windows', name: 'Windows' },
      { id: 'plat-android', name: 'Android' },
      { id: 'plat-macos', name: 'macOS' },
      { id: 'plat-linux', name: 'Linux' },
      { id: 'plat-ios', name: 'iOS' },
    ];
    const depts = Array.from(new Set(devices.map((d) => d.dept || 'Unassigned'))).map((id) => ({ id, name: id }));
    return { platforms, departments: depts };
  }, [devices]);

  const targetDeviceIds = useMemo((): string[] => {
    if (targetScope === 'single') {
      return selectedDeviceId ? [selectedDeviceId] : [];
    }
    if (targetScope === 'multiple') {
      return selectedDeviceIds;
    }
    if (targetScope === 'group') {
      const id = selectedGroupId;
      if (!id) return [];
      if (id.startsWith('plat-')) {
        return devices.filter((d) => deviceMatchesOS(d, id)).map(getDeviceId);
      }
      return devices.filter((d) => (d.dept || 'Unassigned') === id).map(getDeviceId);
    }
    return [];
  }, [targetScope, selectedDeviceId, selectedDeviceIds, selectedGroupId, devices]);

  const selectedDevice = devices.find((d) => getDeviceId(d) === selectedDeviceId);

  useEffect(() => {
    if (targetScope === 'single' && selectedDeviceId) {
      setHistoryLoading(true);
      apiService
        .getGoAgentCommands(selectedDeviceId, { limit: 30 })
        .then((res) => {
          setCommandHistory(res.items || []);
        })
        .catch(() => setCommandHistory([]))
        .finally(() => setHistoryLoading(false));
    } else {
      setCommandHistory([]);
      setSelectedHistoryId(null);
    }
  }, [targetScope, selectedDeviceId]);

  useEffect(() => {
    setSelectedDeviceId('');
    setSelectedDeviceIds([]);
    setSelectedGroupId('');
  }, [targetOS, targetScope]);

  // The backend rejects a command the installed agent build cannot run, so say
  // so before the operator writes a script rather than after they submit it.
  useEffect(() => {
    if (targetScope !== 'single' || !selectedDeviceId) {
      setCapabilities(null);
      return;
    }
    let cancelled = false;
    apiService
      .getDeviceCapabilities(selectedDeviceId)
      .then((matrix) => {
        if (!cancelled) setCapabilities(matrix);
      })
      .catch(() => {
        if (!cancelled) setCapabilities(null);
      });
    return () => {
      cancelled = true;
    };
  }, [targetScope, selectedDeviceId]);

  const commandCapability = capabilities?.capabilities?.command_exec;
  const commandExecUnsupported = commandCapability?.support === 'unsupported';
  const commandExecUnknown = commandCapability?.support === 'unknown';

  const handleExecute = async () => {
    const ids = targetDeviceIds;
    if (ids.length === 0 || !scriptContent.trim()) {
      setLastResult({ success: 0, failed: 0, message: 'Select at least one device and enter a script.' });
      return;
    }

    setExecuting(true);
    setLastResult(null);
    setLastRunOutput(null);

    let successCount = 0;
    let errorCount = 0;
    let lastCommandId: string | null = null;

    for (const deviceId of ids) {
      try {
        const res = await apiService.executeGoAgentCommand(deviceId, {
          command_type: scriptType,
          command: scriptContent.trim(),
          timeout_seconds: timeoutSeconds,
          created_by: 'remote-execution',
        });
        if (res?.command_id) lastCommandId = res.command_id;
        successCount++;
      } catch (e) {
        errorCount++;
      }
    }

    setLastResult({ success: successCount, failed: errorCount });
    setExecuting(false);

    const refreshHistory = () => {
      if (targetScope !== 'single' || !selectedDeviceId) return;
      apiService
        .getGoAgentCommands(selectedDeviceId, { limit: 30 })
        .then((res) => setCommandHistory(res.items || []))
        .catch(() => {});
    };

    refreshHistory();

    // When "Wait for Result" and single device, poll until command completes or timeout.
    // Wait the full script timeout (+ buffer) — do not hard-cap at 2 minutes (that caused
    // false "agent not picking up" errors on long scripts).
    if (waitForResult && targetScope === 'single' && selectedDeviceId && lastCommandId) {
      setLastRunOutput('Waiting for agent to run the command…');
      const waitMs = Math.min(Math.max(timeoutSeconds, 60) * 1000 + 30000, 30 * 60 * 1000);
      const deadline = Date.now() + waitMs;
      let lastStatus = 'pending';
      const poll = async () => {
        if (Date.now() > deadline) {
          if (lastStatus === 'running') {
            setLastRunOutput(
              `Timed out after ${Math.round(waitMs / 1000)}s while the agent was still running the script. ` +
                'Increase “Timeout (seconds)” and click Refresh on Execution History, or uncheck Wait for Result and check history later.'
            );
          } else if (lastStatus === 'pending' || lastStatus === 'queued') {
            setLastRunOutput(
              'Timed out — command is still pending (agent not picking it up). On Windows run as Admin: Restart-Service UEMAgent, then try again.'
            );
          } else {
            setLastRunOutput(
              `Timed out waiting for result (last status: ${lastStatus}). Check Execution History below.`
            );
          }
          refreshHistory();
          return;
        }
        try {
          const res = await apiService.getGoAgentCommands(selectedDeviceId, { limit: 50 });
          const cmd = (res.items || []).find((c: any) => c.command_id === lastCommandId);
          if (cmd?.status) lastStatus = cmd.status;
          const outcome = cmd ? classifyCommandOutcome(cmd.status, cmd.result, cmd.error_message) : 'pending';
          if (cmd && (outcome === 'succeeded' || outcome === 'failed')) {
            const output =
              typeof cmd.result === 'object' && cmd.result != null
                ? (cmd.result.output ?? (cmd.result.exit_code !== undefined ? `Exit code: ${cmd.result.exit_code}` : null) ?? JSON.stringify(cmd.result, null, 2))
                : cmd.result != null
                  ? String(cmd.result)
                  : cmd.error_message || (outcome === 'failed' ? 'Command failed' : '');
            setLastRunOutput(output || (outcome === 'succeeded' ? '(no output)' : 'Command failed'));
            refreshHistory();
            return;
          }
          const elapsedSec = Math.round((waitMs - (deadline - Date.now())) / 1000);
          if (cmd?.status === 'pending' || cmd?.status === 'queued') {
            setLastRunOutput(`Queued — waiting for device agent to pick up command… (${elapsedSec}s)`);
          } else if (cmd?.status === 'running') {
            setLastRunOutput(`Agent is running the command… (${elapsedSec}s / ${timeoutSeconds}s timeout)`);
          }
        } catch (_) {}
        setTimeout(poll, 2000);
      };
      setTimeout(poll, 2000);
    }
  };

  const refreshHistory = () => {
    if (targetScope === 'single' && selectedDeviceId) {
      setHistoryLoading(true);
      apiService
        .getGoAgentCommands(selectedDeviceId, { limit: 30 })
        .then((res) => setCommandHistory(res.items || []))
        .catch(() => setCommandHistory([]))
        .finally(() => setHistoryLoading(false));
    }
  };

  const getStatusIcon = (status: string, result?: unknown, errorMessage?: string | null) => {
    const outcome = classifyCommandOutcome(status, result, errorMessage);
    switch (outcome) {
      case 'succeeded':
        return <CheckCircle size={16} className="text-green-600" />;
      case 'failed':
        return <XCircle size={16} className="text-red-600" />;
      case 'running':
        return <Clock size={16} className="text-blue-600" />;
      default:
        return <AlertCircle size={16} className="text-slate-400" />;
    }
  };

  if (loading) {
    return (
      <HubPage
        title="Remote Execution"
        subtitle="Execute scripts on devices (PowerShell, Bash, CMD)."
        icon={<Terminal size={20} />}
        eyebrow="Ops"
      >
        <HubPanel>
          <div className="flex items-center gap-2 py-8 text-slate-500">
            <span className="inline-block w-5 h-5 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
            Loading devices...
          </div>
        </HubPanel>
      </HubPage>
    );
  }

  return (
    <HubPage
      title="Remote Execution"
      subtitle="Execute scripts on one or more devices. Select target OS and scope, then run your script."
      icon={<Terminal size={20} />}
      eyebrow="Ops"
    >
      <div className="flex flex-col gap-6">
        {/* Two-column: controls left, script editor right */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: controls */}
          <div className="hub-panel">
            <div className="hub-panel__inner space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Select Target OS</label>
              <select
                value={targetOS}
                onChange={(e) => setTargetOS(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-teal-600 text-sm"
              >
                {OS_OPTIONS.map((opt) => (
                  <option key={opt.value || 'all'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-slate-500">
                Only folders, groups, and devices that match the selected OS will be available.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Target Scope</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setTargetScope('single')}
                  className={`flex-1 px-4 py-2.5 rounded-lg border text-sm font-medium transition ${
                    targetScope === 'single'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  Single
                </button>
                <button
                  type="button"
                  onClick={() => setTargetScope('multiple')}
                  className={`flex-1 px-4 py-2.5 rounded-lg border text-sm font-medium transition ${
                    targetScope === 'multiple'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  Multiple
                </button>
                <button
                  type="button"
                  onClick={() => setTargetScope('group')}
                  className={`flex-1 px-4 py-2.5 rounded-lg border text-sm font-medium transition ${
                    targetScope === 'group'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  Group
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {targetScope === 'single' && 'Select Device'}
                {targetScope === 'multiple' && 'Select Devices'}
                {targetScope === 'group' && 'Select Group'}
              </label>
              {targetScope === 'group' ? (
                <select
                  value={selectedGroupId}
                  onChange={(e) => setSelectedGroupId(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="">-- Select Group --</option>
                  <optgroup label="Platform">
                    {deviceGroups.platforms.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({devices.filter((d) => deviceMatchesOS(d, p.id)).length})
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="Department">
                    {deviceGroups.departments.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name} ({devices.filter((x) => (x.dept || 'Unassigned') === d.id).length})
                      </option>
                    ))}
                  </optgroup>
                </select>
              ) : targetScope === 'multiple' ? (
                <DevicePicker
                  mode="multiple"
                  devices={targetOS ? devicesForOS : devices}
                  value={selectedDeviceIds}
                  onChange={setSelectedDeviceIds}
                  platformFilter={targetOS || null}
                  emptyMessage={targetOS ? 'No devices for this OS.' : 'Choose an OS to see device options.'}
                />
              ) : (
                <DevicePicker
                  devices={targetOS ? devicesForOS : devices}
                  value={selectedDeviceId}
                  onChange={setSelectedDeviceId}
                  platformFilter={targetOS || null}
                  emptyMessage={targetOS ? 'No devices found for this OS.' : 'Choose an OS to see device options.'}
                />
              )}
              {targetScope === 'single' && !targetOS && (
                <p className="mt-1 text-xs text-slate-500">Choose an OS to see device options.</p>
              )}
              {targetScope === 'single' && targetOS && devicesForOS.length === 0 && (
                <p className="mt-1 text-xs text-slate-500">No devices found for this OS.</p>
              )}
              {targetScope === 'multiple' && selectedDeviceIds.length > 0 && (
                <p className="mt-1 text-xs text-slate-500">{selectedDeviceIds.length} device(s) selected.</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Execution Options</label>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Script Type</span>
                  <select
                    value={scriptType}
                    onChange={(e) => setScriptType(e.target.value as ScriptType)}
                    disabled={executing}
                    className="px-3 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm w-40"
                  >
                    <option value="powershell">PowerShell</option>
                    <option value="bash">Bash</option>
                    <option value="cmd">CMD</option>
                    <option value="shell">Shell</option>
                  </select>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={runAsAdministrator}
                    onChange={(e) => setRunAsAdministrator(e.target.checked)}
                    disabled={executing}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-700">Run as Administrator</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={waitForResult}
                    onChange={(e) => setWaitForResult(e.target.checked)}
                    disabled={executing}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-700">Wait for Result</span>
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <span className="whitespace-nowrap">Timeout (sec)</span>
                  <input
                    type="number"
                    min={60}
                    max={1800}
                    step={60}
                    value={timeoutSeconds}
                    onChange={(e) => {
                      const n = parseInt(e.target.value, 10);
                      if (!Number.isNaN(n)) setTimeoutSeconds(Math.min(1800, Math.max(60, n)));
                    }}
                    disabled={executing}
                    className="w-20 px-2 py-1 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    title="How long the agent may run the script, and how long the UI waits for output (60–1800)"
                  />
                </label>
              </div>
            </div>

            {commandExecUnsupported && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium">This agent cannot run scripts</p>
                  <p className="text-xs">
                    {commandCapability?.detail ||
                      'The installed agent build does not include remote command execution.'}
                  </p>
                </div>
              </div>
            )}
            {commandExecUnknown && (
              <p className="text-xs text-slate-500">
                This device has not reported its agent version yet, so its capabilities are
                unconfirmed. The command will still be queued.
              </p>
            )}

            <button
              type="button"
              onClick={handleExecute}
              disabled={
                executing ||
                targetDeviceIds.length === 0 ||
                !scriptContent.trim() ||
                commandExecUnsupported
              }
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {executing ? (
                <>
                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Running…
                </>
              ) : (
                <>
                  <Play size={18} fill="currentColor" />
                  Run Script
                </>
              )}
            </button>

            {lastResult && (
              <div
                className={`p-3 rounded-lg border text-sm ${
                  lastResult.failed === 0 ? 'bg-green-50 border-green-200 text-green-800' : lastResult.success === 0 ? 'bg-red-50 border-red-200 text-red-800' : 'bg-amber-50 border-amber-200 text-amber-800'
                }`}
              >
                {lastResult.message ?? `Success: ${lastResult.success}, Failed: ${lastResult.failed}`}
              </div>
            )}
            </div>
          </div>

          {/* Right: script editor and last run output */}
          <div className="flex flex-col gap-4">
            <div className="hub-panel overflow-hidden flex flex-col">
              <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50/50">
                <h3 className="font-semibold text-slate-800 text-sm">{SCRIPT_LABEL[scriptType]}</h3>
                <span className="text-xs font-mono text-slate-500">{SCRIPT_EXT[scriptType]}</span>
              </div>
              <div className="flex-1 min-h-[280px]">
                <textarea
                  value={scriptContent}
                  onChange={(e) => setScriptContent(e.target.value)}
                  placeholder={`# ${scriptType === 'powershell' ? 'PowerShell' : scriptType} script`}
                  disabled={executing}
                  className="w-full h-full min-h-[280px] p-4 bg-slate-800 text-slate-100 font-mono text-sm resize-y focus:ring-0 focus:border-0 border-0 placeholder:text-slate-500"
                  spellCheck={false}
                />
              </div>
            </div>
            {lastRunOutput != null && (
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-4 py-2 border-b border-slate-200 bg-green-50/80">
                  <h3 className="font-semibold text-slate-800 text-sm">Last run output</h3>
                </div>
                <pre className="p-4 bg-slate-800 text-slate-100 font-mono text-xs whitespace-pre-wrap break-words max-h-48 overflow-auto">
                  {lastRunOutput}
                </pre>
              </div>
            )}
          </div>
        </div>

        {/* Execution History (bottom) */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50/50">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <Clock size={18} className="text-slate-500" />
              Execution History
            </h3>
            <button
              type="button"
              onClick={refreshHistory}
              disabled={historyLoading || (targetScope !== 'single' || !selectedDeviceId)}
              className="p-1.5 rounded text-slate-500 hover:bg-slate-200 hover:text-slate-700 disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw size={16} className={historyLoading ? 'animate-spin' : ''} />
            </button>
          </div>
          <div className="p-4 min-h-[120px]">
            {targetScope !== 'single' || !selectedDeviceId ? (
              <p className="text-sm text-slate-400">No execution history yet. Run a script to see history here.</p>
            ) : historyLoading ? (
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <span className="inline-block w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                Loading…
              </div>
            ) : commandHistory.length === 0 ? (
              <p className="text-sm text-slate-400">No execution history yet. Run a script to see history here.</p>
            ) : (
              <ul className="space-y-2">
                {commandHistory.map((cmd) => {
                  const outcome = classifyCommandOutcome(cmd.status, cmd.result, cmd.error_message);
                  const displayStatus = commandOutcomeLabel(outcome);
                  return (
                  <li
                    key={cmd.command_id}
                    onClick={() => setSelectedHistoryId(selectedHistoryId === cmd.command_id ? null : cmd.command_id)}
                    className={`p-3 rounded-lg border cursor-pointer transition ${
                      selectedHistoryId === cmd.command_id ? 'border-blue-400 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {getStatusIcon(cmd.status, cmd.result, cmd.error_message)}
                      <span className="text-xs font-medium uppercase text-slate-600">{cmd.command_type}</span>
                      <span
                        className={`text-xs font-semibold uppercase ${
                          outcome === 'succeeded'
                            ? 'text-green-600'
                            : outcome === 'failed'
                              ? 'text-red-600'
                              : outcome === 'running'
                                ? 'text-blue-600'
                                : 'text-amber-600'
                        }`}
                      >
                        {displayStatus}
                      </span>
                      <span className="text-xs text-slate-400">
                        {cmd.created_at ? new Date(cmd.created_at).toLocaleString() : ''}
                      </span>
                    </div>
                    <pre className="text-xs text-slate-700 whitespace-pre-wrap break-words font-mono bg-white/60 p-2 rounded">
                      {(cmd.command || '').slice(0, 120)}
                      {(cmd.command || '').length > 120 ? '…' : ''}
                    </pre>
                    {selectedHistoryId === cmd.command_id && (
                      <div className="mt-2 pt-2 border-t border-slate-200">
                        <div className="text-xs font-medium text-slate-600 mb-1">Result</div>
                        {cmd.status === 'pending' || cmd.status === 'queued' ? (
                          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded p-2">
                            Waiting for the device agent to pick up this command. If this stays pending,
                            restart the UEM Agent service on the device (Windows: <code>Restart-Service UEMAgent</code>).
                          </p>
                        ) : cmd.result != null && (typeof cmd.result !== 'object' || Object.keys(cmd.result).length > 0) ? (
                          <pre className="text-xs bg-slate-100 p-2 rounded overflow-auto max-h-40 font-mono">
                            {typeof cmd.result === 'object'
                              ? (cmd.result?.output ?? JSON.stringify(cmd.result, null, 2))
                              : String(cmd.result)}
                          </pre>
                        ) : cmd.error_message ? (
                          <pre className="text-xs bg-red-50 text-red-700 p-2 rounded overflow-auto max-h-40 font-mono">
                            {cmd.error_message}
                          </pre>
                        ) : (
                          <p className="text-xs text-slate-500">No output returned.</p>
                        )}
                      </div>
                    )}
                  </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>
    </HubPage>
  );
};

export default RemoteExecutionHub;
